/**
 * The one call both bid pages make.
 *
 * Dispatchers Bids and Drivers Bids are the same read against two append-only tables: the same window,
 * the same paging, the same search, the same `{ success, data }` envelope. Only the path and the row type
 * differ. Written out twice they would be two places to fix the next thing learned about either — which
 * is how the six copies of the bid-driver shape started on the server side.
 *
 * CLAUDE.md: components and hooks call the app's API layer, never axios directly.
 */
import axios from 'axios'
import { apiErrorMessage } from '@/api/errorMessage'

export interface BidListQuery {
    /** One of `ANALYTICS_WINDOW_DAYS`, which the server validates against the same constant. 0 is all time. */
    days: number
    page: number
    limit: number
    search?: string
}

/** `TPage` is `DispatcherBidsPage` or `DriverBidsPage` — the same envelope either way. */
export async function fetchBidList<TPage>(path: string, query: BidListQuery, signal?: AbortSignal): Promise<TPage> {
    try {
        const res = await axios.get<{ success: boolean; data: TPage }>(path, {
            // Blank filters are omitted rather than sent empty, so the server sees "no filter" instead of
            // having to treat '' as one.
            params: {
                days: query.days,
                page: query.page,
                limit: query.limit,
                ...(query.search ? { search: query.search } : {}),
            },
            signal,
        })
        return res.data.data
    } catch (error) {
        // `apiErrorMessage` rather than another copy of this catch block.
        throw new Error(apiErrorMessage(error))
    }
}
